import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLoading } from './LoadingOverlay/LoadingContext';
import { waitForNextPaint } from './LoadingOverlay/UtilsLoadingOverlay';
import { useAuth } from 'react-oidc-context';
import { getDisplayUserName } from '../utils/authHelpers';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const OrderPage = () =>{  // 購入依頼ページ用コンポーネント
 // =================
 // SSO認証データ取得
 // =================
  // SSO認証データ活用宣言 
  const auth = useAuth();
  // SSO認証からEmailアドレス取得
  const clientEmail = auth.user?.profile?.email ?? "";
  // SSO認証から部署コードデータ取得
  const ext13 = auth.user?.profile?.["custom:extentionattribute13"] ?? "";
  // SSO認証からSignIn者の名前（表示用ユーザー名（認証時））取得
  // ---共通ヘルパーで custom:displayname の漢字部を取得---
  const userName = getDisplayUserName(auth?.user?.profile ?? {});

  // =========
  // 基本設定
  // =========
  const { showLoading, hideLoading } = useLoading();  // LoadingOverlayをセット
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);  // DBからデータリストを取得
  const [isFetchingList, setIsFetchingList] = useState(false);

  const [filterLocationCode, setFilterLocationCode] = useState('すべて');
  const [filterCategory, setFilterCategory] = useState('すべて');
  const [filterPersonInCharge, setFilterPersonInCharge] = useState('すべて');
  const [editBuffers, setEditBuffers] = useState({});
  const [selectedProductNames, setSelectedProductNames] = useState([]); // 行選択用（複数可）
  const [showConfirmModal, setShowConfirmModal] = useState(false); // 購入依頼内容確認モーダル表示
  const [sortOrder, setSortOrder] = useState('asc');  // 品名ソート用
  const [sortByProductName, setSortByProductName] = useState(false);  // 品名でソート中か

  const confirmModalRef = useRef(null);
  // 表示データ更新とセッションストレージ保存を一括で行うヘルパー
  const updateDataAndSession = (updater) => {
    setData(prev => {
      const nextData = typeof updater === 'function' ? updater(prev) : updater;
      sessionStorage.setItem(keyname, JSON.stringify(nextData));
      return nextData;
    });
  };
  
  // 保管場所の値を抽出（dataから動的に取得）
  const locationcodes = Array.from(new Set(data.map(row => row.LocationCode).filter(Boolean)));
  // 分類の値を抽出（dataから動的に取得）
  const categories = Array.from(new Set(data.map(row => row.Category).filter(Boolean)));
  // 担当者の値を抽出（dataから動的に取得）
  const personincharges = Array.from(new Set(data.map(row => row.PersonInCharge).filter(Boolean)));

  // フィルタ適用
  let filtered = data;
  if (filterLocationCode !== 'すべて') {
    filtered = filtered.filter(row => row.LocationCode === filterLocationCode);
  }
  if (filterCategory !== 'すべて') {
    filtered = filtered.filter(row => row.Category === filterCategory);
  }
  if (filterPersonInCharge !== 'すべて') {
    filtered = filtered.filter(row => row.PersonInCharge === filterPersonInCharge);
  }
  
  // フィルタ切り替え
  const handleFilterLocationCode = e => setFilterLocationCode(e.target.value);
  const handleFilterCategory = e => setFilterCategory(e.target.value);
  const handleFilterPersonInCharge = e => setFilterPersonInCharge(e.target.value);

  const keyname = 'OrderPage_dataList';  // セッションストレージのキー名（ページ専用）
  // 固定のAPIエンドポイントを指定
  const url = `https://kd4c0h9pca.execute-api.ap-northeast-1.amazonaws.com/Dev/LambdaToDynamoDB_DataEdit`;
  // 固定URLにクエリパラメータを追加（Lambda関数でpage_nameを使って分岐させるため）
  const urlWithParams = new URL(url);
  urlWithParams.searchParams.set('page_name', 'OrderPage');
  // 購入依頼メール送信APIエンドポイントを指定
  const url2 = `https://kd4c0h9pca.execute-api.ap-northeast-1.amazonaws.com/Dev/LambdaToSES_Sendmail`;

  // ========
  // API設定
  // ========
  // タイムアウト付きHTTPリクエスト関数
  const fetchWithTimeout = async (urlWithParams, options = {}, timeout = 5000) => {
    const controller = new AbortController();  // タイムアウト用コントローラーを作成
    const signal = controller.signal;  // fetchに渡すsignalを取得
    const fetchPromise = fetch(urlWithParams, { ...options, signal });  // fetchをsignal付きで渡す
    const timeoutId = setTimeout(() => {
      controller.abort();  // タイムアウトが来たらfetchを中断
    }, timeout);
    try {
      return await fetchPromise;  // fetchの結果を待つ
    } finally {
      clearTimeout(timeoutId);  // fetchが完了したらタイマーを解除
    }
  };

  // 再トライ付きAPI呼び出し関数（GET/POST両対応）
  const fetchAPI = async (urlWithParams, options = {}, retries = 2, timeout = 5000) => {
    let attempts = 0;
    let lastError = null;
    while (attempts < retries) {
      try {
        const response = await fetchWithTimeout(urlWithParams, options, timeout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();  // 成功時にJSONデータを返す
      } catch (error) {
        lastError = error;
        attempts += 1;
        console.warn(`Attempt ${attempts} failed: ${error.message}`);
        if (attempts >= retries) {
            throw lastError;
        }
      }
    }
  };

  // 将来使用予定
  // ログインしていないユーザーをログインページへリダイレクトする
  // useEffect(() => {
  //   if (!auth) {
  //       navigate('/login');
  //   }
  // }, [auth, navigate]);

  // ===========
  // データ取得
  // ===========
  const GetList = useCallback(async ({ withLoading = true } ={}) => {
    setIsFetchingList(true);  // テーブルを隠す（非表示）
    if (withLoading) showLoading('データ読み込み中...');  // ユーザーに見せるメッセージ
    setError(null);
 
    // APIメソッドとデータ形式を特定
    const options = {
        method: 'GET',  // GETまたはPOST
        headers: {
            'Content-Type': 'application/json',  // 送受信するデータをJSON形式とするを示す
        },
    };

    try {
      const result = await fetchAPI(urlWithParams,options);
      console.log("GetList:::",result);
      
      // レスポンス形式を柔軟に処理
      let items = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result.Items && Array.isArray(result.Items)) {
        items = result.Items;
      } else if (result.body) {
        // Lambda関数がbodyプロパティでデータを返す場合
        const bodyData = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
        items = Array.isArray(bodyData) ? bodyData : (bodyData.Items || []);
      } else {
        console.warn('予期しないレスポンス形式:', result);
        items = [];
      }
      
      // データの形式を統一（Category～Notes）
      const formattedData = items.map(item => ({
        LocationCode: item.LocationCode || item.locationcode || '',
        Category: item.Category || item.category || '',
        ProductName: item.ProductName || item.productname || '',
        ProductNumber: item.ProductNumber || item.productnumber || '',
        UnitPrice: item.UnitPrice || item.unitprice || '',
        OrderQuantity: item.OrderQuantity || item.orderquantity || '',
        DueDate: item.DueDate || item.duedate || '',
        PersonInCharge: item.PersonInCharge || item.personincharge || '',
        Notes: item.Notes || item.notes || '',
        Comment: item.Comment || item.comment || ''
      }));
      
      console.log('フォーマット済みデータ:', formattedData);
      setData(formattedData);  // 統一されたstateに保存
      setEditBuffers({});
    
      // データをブラウザのセッションストレージに保存
      // ページをリロードしても、同じセッション中ならデータを保持できる
      console.log('keyname:::',keyname);
      sessionStorage.setItem(keyname, JSON.stringify(formattedData));
    } catch (error) {
        console.error('API呼び出しエラー:', error);
        const errorMessage = error.message || 'データの取得に失敗しました。しばらくしてから再度お試しください。';
        setError(errorMessage);
        setData([]);  // エラー時は空の配列を設定
    } finally {
      setIsFetchingList(false);  // テーブルを戻す（表示）
      hideLoading();
    }
  }, [showLoading, hideLoading, keyname]);

  // =================
  // コメント編集・削除
  // =================
  const startEditRow = (row) => {
    if (!row || !row.ProductName || editBuffers[row.ProductName]) return;
    const normalized = { Comment: row.Comment || '' };
    setEditBuffers(prev => ({ ...prev, [row.ProductName]: normalized }));
  };

  const handleEditFieldChange = (productname, field, value) => {
    if (!productname) return;
    setEditBuffers(prev => ({
      ...prev,
      [productname]: {
        ...(prev[productname] || { Comment: '' }),
        [field]: value
      }
    }));
  };

  const hasRowChanges = (row) => {
    if (!row || !row.ProductName || !editBuffers[row.ProductName]) return false;
    const buffer = editBuffers[row.ProductName];
    return (buffer.Comment ?? '') !== (row.Comment ?? '');
  };

  const clearEditStateForRow = (productname) => {
    setEditBuffers(prev => {
      const updated = { ...prev };
      delete updated[productname];
      return updated;
    });
  };

  const UpdateRole = (row) => {
    if (!row || !row.ProductName || !editBuffers[row.ProductName] || !hasRowChanges(row)) return;
    const buffer = editBuffers[row.ProductName];
    updateDataAndSession(prev =>
      prev.map(item =>
        item.ProductName === row.ProductName
          ? { ...item, Comment: buffer.Comment ?? '' }
          : item
      )
    );
    clearEditStateForRow(row.ProductName);
  };

  const DeleteRole = (row) => {
    if (!row || !row.ProductName) return;
    updateDataAndSession(prev =>
      prev.map(item =>
        item.ProductName === row.ProductName
          ? { ...item, Comment: '' }
          : item
      )
    );
    clearEditStateForRow(row.ProductName);
  };

  // ====================
  // 初回マウント時の処置
  // ====================
  // 初回マウント時にセッションストレージからデータを取得し、画面表示。 データがなければAPIを呼び出す
  useEffect(() => {
    const datalist = sessionStorage.getItem(keyname);
    // Datalistが存在し、内容に"null"という文字がないこと
    if (datalist && datalist != 'null') {  // セッションストレージ内にNullが生成されるため。その状態でlistに遷移するとmapできずエラーとなるため回避
      // Reactステートに取得データをセット（統一されたstateに保存）
      setData(JSON.parse(datalist));
        console.log("リスト取得完了");
    } else {
        // 有効なデータがない場合はAPIから取得
        GetList();
    }
  }, []);

  console.log("data:::",data);

  // =========
  // 表の機能
  // =========
  // 【共通消耗品リスト】
  // ソート処理（フィルタ適用後）
  const sortedData = (sortByProductName
    ? [...filtered].sort((a, b) => {
        if (a.ProductName < b.ProductName) return sortOrder === 'asc' ? -1 : 1;
        if (a.ProductName > b.ProductName) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      })
    : filtered);
  // フィルタで非表示になっている行も含めて選択された購入依頼品を取得する
  const selectedRows = data.filter(row => selectedProductNames.includes(row.ProductName));
  const totalAmount = selectedRows.reduce((sum, row) => {
    const unitPrice = typeof row.UnitPrice === 'number'
      ? row.UnitPrice
      : Number((row.UnitPrice || '').toString().replace(/,/g, '')) || 0;
    const orderQuantity = typeof row.OrderQuantity === 'number'
      ? row.OrderQuantity
      : Number((row.OrderQuantity || '').toString().replace(/,/g, '')) || 0;
    const value = (isNaN(unitPrice) ? 0 : unitPrice) * (isNaN(orderQuantity) ? 0 : orderQuantity);
    return sum + value;
  }, 0);

  // 品名ヘッダークリック時
  const handleProductNameSort = () => {
    if (!sortByProductName) {
      setSortByProductName(true);
      setSortOrder('asc');
    } else {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    }
  };

  // 購入依頼ボタン押下時処理（url2 へメール送信依頼）
  const handlePurchaseRequest = async () => {
    if (selectedRows.length === 0) {
      alert('購入依頼品が選択されていません。');
      return;
    }

    // モーダルに表示している全担当者名（重複は除外）
    const uniqueAssignees = Array.from(
      new Set(
        selectedRows
          .map(row => row.PersonInCharge)
          .filter(Boolean)
      )
    );

    // モーダル表示画面を A4 サイズで PDF 化（DOMをキャプチャ）
    let pdfBase64 = '';
    let pdfFileName = '';
    try {
      const element = confirmModalRef.current;
      if (!element) {
        throw new Error('confirmModalRef is not set');
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/jpeg',0.8);

      // A4: 210mm x 297mm
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = usableWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let y = margin;
      let remaining = imgHeight;

      // 1ページ目
      pdf.addImage(imgData, 'jpeg', margin, y, imgWidth, imgHeight);
      remaining -= (pageHeight - margin * 2);

      // 2ページ目以降（画像を上にずらして描画）
      let offsetY = pageHeight - margin * 2;
      while (remaining > 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'jpeg', margin, margin - offsetY, imgWidth, imgHeight);
        remaining -= (pageHeight - margin * 2);
        offsetY += (pageHeight - margin * 2);
      }

      const pdfDataUri = pdf.output('datauristring'); // data:application/pdf;base64,...
      pdfBase64 = pdfDataUri.split(',')[1] || '';
      const ts = new Date().toISOString().split('T')[0].replace(/-/g, '');
      pdfFileName = `共通消耗品購入依頼書_${ts}.pdf`;
    } catch (e) {
      console.error('PDF生成エラー:', e);
      alert('PDFの作成に失敗しました。');
      return;
    }

    // Lambda 側指定に合わせて assignee1, assignee2,... 形式で送信（PDFも同送）
    const payload = {
      clientEmail,
      pdfBase64,
      pdfFileName,
      pdfMimeType: 'application/pdf'
    };
    uniqueAssignees.forEach((name, idx) => {
      payload[`assignee${idx + 1}`] = name;
    });

    // 対象担当者が取得できない場合は送信しない
    if (uniqueAssignees.length === 0) {
      alert('担当者が取得できませんでした。');
      return;
    }

    showLoading('送信中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };

    let success = false;

    try {
      const response = await fetchWithTimeout(url2, options, 5000);

      success = true;

      // HTTP ステータスがエラーの場合
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('購入依頼送信エラー (HTTP):', response.status, text);
        alert('購入依頼の送信に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      // JSON 形式ならログに出す（必須ではない）
      try {
        const result = await response.json();
        console.log('購入依頼送信結果:', result);
      } catch (e) {
        // JSON でないレスポンスは無視（成功扱い）
        console.log('購入依頼送信結果（非JSONレスポンス）');
      }
      //alert('購入依頼を送信しました。');
      //setShowConfirmModal(false);

    } catch (error) {
      console.error('購入依頼送信エラー (network):', error);
      alert('購入依頼の送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('購入依頼を送信しました。');
      setShowConfirmModal(false);
    }
  };

  return (
    <div className='wrapper'>
      <h2 className='page-title'>購入依頼</h2>
      
      {/* エラー表示 */}
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          fontSize: '0.6em', 
          color: '#ff4444',
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff4444',
          borderRadius: '4px',
          margin: '10px 0'
        }}>
          {error}
          <button 
            onClick={GetList}
            style={{
              marginLeft: '10px',
              padding: '4px 8px',
              fontSize: '0.5em',
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            再試行
          </button>
        </div>
      )}
      
      {/* ローディング中やエラー時はテーブルを非表示 */}
      {!isFetchingList && !error && (
        <div className='content-area'>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <h3 className='table-title' style={{ margin: '10px 0 0 0.3em' }}>共通消耗品リスト　　　　　　　【購入依頼品を選択ください】</h3>
            <button
              style={{
                padding: '4px 8px',
                fontSize: '0.6em', // h3と同サイズ
                backgroundColor: '#1971c2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                margin: '0 0.3em 0.3em 0',
                lineHeight: '1.4'
              }}
              onClick={() => setShowConfirmModal(true)}
            >
              購入依頼内容確認
            </button>
          </div>
          <div className='table-area'>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>保管場所</span>
                      <select value={filterLocationCode} onChange={handleFilterLocationCode} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                        {locationcodes.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>分類</span>
                      <select value={filterCategory} onChange={handleFilterCategory} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                      </select>
                    </div>
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={handleProductNameSort}
                  >
                    品名
                    <span style={{ color: sortOrder === 'asc' && sortByProductName ? '#222' : '#888', marginLeft: '2px' }}>▲</span>
                    <span style={{ color: sortOrder === 'desc' && sortByProductName ? '#222' : '#888' }}>▼</span>
                  </th>
                  <th>品番</th>
                  <th>納期（目安）</th>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>担当者</span>
                      <select value={filterPersonInCharge} onChange={handleFilterPersonInCharge} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                        {personincharges.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th>備考</th>
                  <th>コメント</th>
                  <th>コメント編集</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => {
                  const isEditing = !!editBuffers[row.ProductName];
                  const buffer = editBuffers[row.ProductName] || {};
                  const canUpdate = isEditing && hasRowChanges(row);
                  const isSelected = selectedProductNames.includes(row.ProductName);
                  return (
                    <tr
                      key={idx}
                      onClick={() =>
                        setSelectedProductNames(prev =>
                          prev.includes(row.ProductName)
                          ? prev.filter(name => name !== row.ProductName)
                          : [...prev, row.ProductName]
                        )
                      }
                      style={{
                        backgroundColor: isSelected ? '#fff3bf' : undefined,
                        color: isSelected ? '#222222' : undefined
                      }}
                    >
                      <td>{row.LocationCode}</td>
                      <td>{row.Category}</td>
                      <td>{row.ProductName}</td>
                      <td>{row.ProductNumber}</td>
                      <td>{row.DueDate}</td>
                      <td>{row.PersonInCharge}</td>
                      <td>{row.Notes}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Comment ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Comment', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Comment}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button 
                            style={{ 
                              backgroundColor: '#44aa44', 
                              opacity: isEditing ? 0.6 : 1,
                              cursor: isEditing ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isEditing}
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditRow(row);
                            }}
                          >
                            {isEditing ? '入力中' : '入力'}
                          </button>
                          {isEditing && (
                            <button 
                              style={{ 
                                backgroundColor: '#888888', 
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                clearEditStateForRow(row.ProductName);
                              }}
                            >
                              キャンセル
                            </button>
                          )}
                          <button 
                            style={{ 
                              backgroundColor: canUpdate ? '#4444ff' : '#9aa0ff', 
                              cursor: canUpdate ? 'pointer' : 'not-allowed',
                              opacity: canUpdate ? 1 : 0.6
                            }}
                            disabled={!canUpdate}
                            onClick={(e) => {
                              e.stopPropagation();
                              UpdateRole(row);
                            }}
                          >
                            確定
                          </button>
                          <button 
                            style={{ 
                              backgroundColor: '#ff4444', 
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              DeleteRole(row);
                            }}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {/* 購入依頼内容確認モーダル */}
          {showConfirmModal && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
              }}
              onClick={() => setShowConfirmModal(false)}
            >
              <div
                style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  width: '90%',
                  maxWidth: '960px',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  color: '#222'
                }}
                onClick={(e) => e.stopPropagation()}
                ref={confirmModalRef}
              >
                {/* タイトル＋操作ボタン行（タイトルはこの下の承認欄ヘッダーと高さを揃える） */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.7em',
                        backgroundColor: '#888888',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setShowConfirmModal(false)}
                    >
                      戻る
                    </button>
                    <button
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.7em',
                        backgroundColor: '#1971c2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={handlePurchaseRequest}
                    >
                      購入依頼
                    </button>
                  </div>
                </div>
                {/* タイトル＋承認欄（承認者・確認者・担当者）＋合計金額（下ラインを揃える） */}
                <div
                  style={{
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: '16px'
                  }}
                >
                  {/* 左側：タイトル＋合計金額（縦並び） */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '4px'
                    }}
                  >
                    {/* タイトル（承認欄の表ヘッダーと高さを揃える） */}
                    <h3
                      style={{
                        margin: 0,
                        fontWeight: 'bold',
                        fontSize: '0.8em',
                        lineHeight: '56px' // 承認欄ヘッダーの高さに合わせる
                      }}
                    >
                      購入依頼品リスト
                    </h3>
                    {/* 合計金額（承認欄とリスト表ヘッダーの間に表示、承認欄の下ラインと揃える） */}
                    <div
                      style={{
                        fontSize: '0.75em',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingBottom: '4px'
                      }}
                    >
                      <span style={{ fontWeight: 'bold', borderBottom: '1px solid #ccc' }}>合計金額：</span>
                      <span style={{ borderBottom: '1px solid #ccc' }}>{totalAmount.toLocaleString()} 円</span>
                      <span style={{ fontWeight: 'bold', marginLeft: '40px', borderBottom: '1px solid #ccc' }}>購入依頼者：</span>
                      <span style={{ borderBottom: '1px solid #ccc' }}>{ext13}_{userName}</span>
                    </div>
                  </div>
                  {/* 右側：承認テーブル（右寄せ） */}
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      marginLeft: 'auto',
                      fontSize: '0.7em'
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            border: '1px solid #ccc',
                            padding: '4px 8px',
                            backgroundColor: '#f5f5f5',
                            color: '#222',
                            minWidth: '120px',
                            textAlign: 'center'
                          }}
                        >
                          担当者
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          style={{
                            border: '1px solid #ccc',
                            padding: '12px 8px',
                            height: '32px'
                          }}
                        ></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.65em' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>分類</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>品名</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>品番</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>金額（円）</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>納期（目安）</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>担当者</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>備考</th>
                      <th style={{ border: '1px solid #ccc', padding: '6px', backgroundColor: '#e6e6e6', color: '#222' }}>コメント</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRows.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center', color: '#666' }}>
                          購入依頼品が選択されていません。
                        </td>
                      </tr>
                    ) : (
                      selectedRows.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.Category}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.ProductName}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.ProductNumber}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>
                            {(() => {
                              const up = typeof row.UnitPrice === 'number' ? row.UnitPrice : Number((row.UnitPrice || '').toString().replace(/,/g, ''));
                              const qty = typeof row.OrderQuantity === 'number' ? row.OrderQuantity : Number((row.OrderQuantity || '').toString().replace(/,/g, '')) || 0;
                              const amount = (isNaN(up) ? 0 : up) * (isNaN(qty) ? 0 : qty);
                              return amount.toLocaleString();
                            })()}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.DueDate}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.PersonInCharge}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.Notes}</td>
                          <td style={{ border: '1px solid #ccc', padding: '6px', color: '#222' }}>{row.Comment}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '0.65em', color: '#222' }}>End</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPage;
